import { useCallback, useEffect } from 'react';

const DEFAULT_MESSAGE = 'You have unsaved changes. Discard them and leave?';

/**
 * Warns the user before leaving the page (tab close, refresh, external nav)
 * when `isDirty` is true, and exposes a `confirmNav` helper for components
 * to call before triggering in-app navigation (e.g. a Cancel button or a
 * "Back to list" link).
 *
 * Why a `confirmNav` helper instead of an automatic in-app blocker:
 * react-router-dom v6's `useBlocker` only works inside a data router
 * (`createBrowserRouter`). This app uses the legacy `<BrowserRouter>`, which
 * has no public hook for blocking in-app navigation. Until/unless we migrate
 * the router, callers must opt in by wrapping their navigation handlers.
 *
 * Usage:
 * ```tsx
 * const { confirmNav } = useUnsavedChangesPrompt(isDirty);
 *
 * // Tab close / refresh: handled automatically by the hook.
 * // In-app nav (e.g. on a "Back" button):
 * onClick={() => { if (confirmNav()) navigate('/dealers'); }}
 * ```
 */
export function useUnsavedChangesPrompt(isDirty: boolean) {
  useEffect(() => {
    if (!isDirty) return;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Some older browsers require returnValue to be set; modern browsers
      // ignore the value and show their own message.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const confirmNav = useCallback(
    (message: string = DEFAULT_MESSAGE) => {
      if (!isDirty) return true;
      return window.confirm(message);
    },
    [isDirty]
  );

  return { confirmNav };
}
